import { List, Map, fromJS } from 'immutable';
import isarray from 'isarray';
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  takeEvery,
} from 'redux-saga/effects';
import { action, dispatch, regHandlers, regSaga, store } from '../../store';
import { mountForm, submitForm, unmountForm } from '..';
import { setValue } from '../form/Form';

export const hasData = data => isarray(data) || data instanceof List;
const noop = () => null;
const getDataSource = tableData => {
  const tableOptions = tableData.get('tableOptions');
  const dataSourceFn = tableData.get('dataSource') || noop;

  return dataSourceFn(tableOptions);
};

export const isClientSide = (tableData, ignoreData) => {
  const data = tableData.get('data');
  const dataSource = getDataSource(tableData);

  return (
    (hasData(data) || ignoreData) &&
    ((dataSource &&
      (dataSource.clientSideSearch === true || dataSource.clientSide)) ||
      !dataSource)
  );
};

export const clientSideGotoPage = (tableData, pageNumber) => {
  const data = tableData.get('data');
  const pageSize = tableData.get('pageSize');
  const newOffset = (pageNumber - 1) * pageSize;

  if (typeof pageNumber !== 'number' || pageNumber < 1) {
    return tableData;
  }

  if (newOffset > data.size) return tableData;

  return tableData.set('pageOffset', newOffset);
};

const clientSideNextPage = tableData =>
  tableData.update(
    'pageOffset',
    pageOffset => pageOffset + tableData.get('pageSize'),
  );

const clientSidePrevPage = tableData =>
  tableData.update('pageOffset', pageOffset =>
    Math.max(0, pageOffset - tableData.get('pageSize')),
  );

export const serverSideGotoPage = (tableData, pageNumber) => {
  const pageTokens = tableData.get('pageTokens');
  const tokenLocation = pageNumber - 2;

  if (
    typeof pageNumber !== 'number' ||
    pageNumber < 1 ||
    pageNumber > pageTokens.size
  ) {
    return tableData;
  }

  if (pageNumber === 1) {
    return tableData
      .set('loading', true)
      .set('nextPageToken', null)
      .set('pageTokens', List());
  }

  return tableData
    .set('loading', true)
    .set('nextPageToken', pageTokens.get(tokenLocation))
    .set('pageTokens', pageTokens.slice(0, tokenLocation));
};

const serverSideNextPage = tableData =>
  tableData
    .set('loading', true)
    .set('nextPageToken', tableData.get('currentPageToken'))
    .update('pageTokens', pt => pt.push(tableData.get('currentPageToken')));

const serverSidePrevPage = tableData =>
  tableData
    .set('loading', true)
    .update('pageTokens', pt => pt.pop())
    .update(t => t.set('nextPageToken', t.get('pageTokens').last()));

const serverSideReloadPage = tableData =>
  tableData
    .set('loading', true)
    .set('nextPageToken', tableData.get('pageTokens').last());

// should be '' except if op is between, or in
const getInitialFilterValue = column =>
  column.has('initial')
    ? column.get('initial')
    : column.get('filter') === 'between'
      ? List(['', ''])
      : column.get('filter') === 'in'
        ? List()
        : '';

export const generateFilters = (tableKey, columns) =>
  Map(
    columns
      .filter(c => c.get('filter'))
      .reduce(
        (filters, column) =>
          filters.set(
            column.get('value'),
            Map({
              value: getInitialFilterValue(column),
              column,
            }),
          ),
        Map(),
      ),
  );

const evaluateValidFilters = table => {
  const validateFilters = table.get('onValidateFilters');
  return validateFilters ? validateFilters(table.get('filters')) : true;
};

export const generateInitialSortColumn = (sortColumn, columns) =>
  sortColumn ? columns.find(col => col.get('value') === sortColumn) : null;

regHandlers({
  MOUNT_TABLE: (state, { payload: { tableKey } }) => {
    return state.setIn(['tables', tableKey, 'mounted'], true);
  },
  UNMOUNT_TABLE: (state, { payload: { tableKey } }) => {
    return state.deleteIn(['tables', tableKey]);
  },
  CONFIGURE_TABLE: (
    state,
    {
      payload: {
        tableKey,
        data,
        dataSource,
        columns,
        columnSet,
        pageSize = 25,
        defaultSortColumn = null,
        defaultSortDirection = 'asc',
        tableOptions,
        onValidateFilters,
        filterForm,
        filterSet,
        onFetch,
        onColumnSort,
        onColumnToggle,
      },
    },
  ) =>
    !state.getIn(['tables', tableKey, 'mounted'])
      ? state
      : state.hasIn(['tables', tableKey, 'configured'])
        ? state.setIn(['tables', tableKey, 'initialize'], false)
        : state.mergeIn(
            ['tables', tableKey],
            Map({
              data: hasData(data) ? fromJS(data) : data,
              extraData: null,
              dataSource,
              tableOptions,
              columns,
              rows: List(),
              columnSet,

              initializing: true,
              loading: true,

              pageSize,
              sortColumn: generateInitialSortColumn(defaultSortColumn, columns),
              sortDirection: defaultSortDirection,

              // Pagination
              currentPageToken: null,
              nextPageToken: null,
              pageTokens: List(),
              pageOffset: 0,
              count: null,
              error: null,

              // Filtering
              filterForm,
              filterSet,
              filters: generateFilters(tableKey, columns),
              appliedFilters: generateFilters(tableKey, columns),
              validFilters: true,
              onValidateFilters,

              onFetch,
              onColumnSort,
              onColumnToggle,

              configured: true,
              initialize: true,
            }),
          ),
  TOGGLE_COLUMN: (state, { payload: { tableKey, column } }) =>
    state.updateIn(['tables', tableKey, 'columnSet'], columnSet =>
      state
        // Get all columns
        .getIn(['tables', tableKey, 'columns'])
        // Filter to only columns that are toggleable or in the current
        // columnSet, while toggling the current column if it's toggleable
        .filter(c =>
          !c.get('toggleable')
            ? columnSet.includes(c.get('value'))
            : columnSet.includes(c.get('value'))
              ? c.get('value') !== column
              : c.get('value') === column,
        )
        // Map to columns values to get the new columnSet
        .map(c => c.get('value')),
    ),
  TOGGLE_FILTER: (state, { payload: { tableKey, filters } }) =>
    state.updateIn(['tables', tableKey, 'filterSet'], filterSet =>
      !filterSet
        ? filters
        : filters.reduce(
            (set, filter) =>
              set.includes(filter)
                ? set.filter(f => f !== filter)
                : [...set, filter],
            filterSet,
          ),
    ),
  SET_ROWS: (
    state,
    {
      payload: {
        tableKey,
        rows,
        data,
        extraData = null,
        nextPageToken,
        count,
        error = null,
      },
    },
  ) =>
    state.updateIn(
      ['tables', tableKey],
      table =>
        table &&
        table
          .set('rows', rows)
          .set('data', data)
          .set('extraData', extraData)
          .set('currentPageToken', nextPageToken)
          .set('nextPageToken', null)
          .set('count', count)
          .set('error', error)
          .set('initializing', false)
          .set('loading', false),
    ),
  NEXT_PAGE: (state, { payload: { tableKey } }) =>
    state
      .updateIn(['tables', tableKey], tableData =>
        isClientSide(tableData)
          ? clientSideNextPage(tableData)
          : serverSideNextPage(tableData),
      )
      .setIn(['tables', tableKey, 'error'], null),
  PREV_PAGE: (state, { payload: { tableKey } }) =>
    state
      .updateIn(['tables', tableKey], tableData =>
        isClientSide(tableData)
          ? clientSidePrevPage(tableData)
          : serverSidePrevPage(tableData),
      )
      .setIn(['tables', tableKey, 'error'], null),
  GOTO_PAGE: (state, { payload: { tableKey, pageNumber } }) =>
    state
      .updateIn(['tables', tableKey], tableData =>
        isClientSide(tableData)
          ? clientSideGotoPage(tableData, pageNumber)
          : serverSideGotoPage(tableData, pageNumber),
      )
      .setIn(['tables', tableKey, 'error'], null),
  RELOAD_PAGE: (state, { payload: { tableKey } }) =>
    state.hasIn(['tables', tableKey])
      ? state.updateIn(['tables', tableKey], tableData =>
          tableData.get('configured')
            ? (isClientSide(tableData)
                ? tableData
                : serverSideReloadPage(tableData)
              )
                .set('loading', true)
                .set('data', null)
                .set('error', null)
            : tableData,
        )
      : state,
  SORT_COLUMN: (state, { payload: { tableKey, column } }) =>
    state.updateIn(['tables', tableKey], t => {
      const sortColumn = t.get('sortColumn');
      const sortDirection = t.get('sortDirection');
      return (
        t
          // When sorting changes, reset pagination.
          .set('pageOffset', 0)
          .set('currentPageToken', null)
          .set('nextPageToken', null)
          .set('pageTokens', List())
          // Update the sort column / direction.
          .set(
            'sortDirection',
            sortColumn === column
              ? sortDirection === 'asc'
                ? 'desc'
                : 'asc'
              : 'asc',
          )
          .set('sortColumn', column)
          .set('error', null)
      );
    }),
  SET_FILTER: (state, { payload: { tableKey, filter, value } }) =>
    state.updateIn(['tables', tableKey], table =>
      table
        .setIn(['filters', filter, 'value'], value)
        .set('validFilters', evaluateValidFilters(table)),
    ),
  APPLY_FILTERS: (state, { payload: { tableKey } }) =>
    state.updateIn(['tables', tableKey], table =>
      table
        .set('loading', true)
        .set('appliedFilters', state.getIn(['tables', tableKey, 'filters']))
        .set('pageOffset', 0)
        .set('currentPageToken', null)
        .set('nextPageToken', null)
        .set('pageTokens', List())
        .set('error', null),
    ),
  APPLY_FILTER_FORM: (state, { payload: { tableKey, appliedFilters } }) =>
    state.updateIn(['tables', tableKey], table =>
      table
        .set('loading', true)
        .set('appliedFilters', appliedFilters)
        .set('pageOffset', 0)
        .set('currentPageToken', null)
        .set('nextPageToken', null)
        .set('pageTokens', List())
        .set('error', null),
    ),
  REFETCH_TABLE_DATA: (state, { payload: { tableKey } }) =>
    state.hasIn(['tables', tableKey])
      ? state.updateIn(['tables', tableKey], tableData =>
          tableData.get('dataSource')
            ? tableData
                .set('loading', true)
                .set('pageOffset', 0)
                .set('currentPageToken', null)
                .set('nextPageToken', null)
                .set('pageTokens', List())
                .set('data', null)
                .set('error', null)
            : tableData,
        )
      : state,
  CLEAR_TABLE_FILTERS: (state, { payload: { tableKey } }) =>
    state.setIn(
      ['tables', tableKey, 'filters'],
      generateFilters(tableKey, state.getIn(['tables', tableKey, 'columns'])),
    ),
  CLEAR_SELECTED_FILTERS: (state, { payload: { tableKey } }) =>
    state.setIn(
      ['tables', tableKey, 'filters'],
      state.getIn(['tables', tableKey, 'appliedFilters']),
    ),
});

function* calculateRowsTask({ type, payload }) {
  try {
    const { tableKey } = payload;
    const tableData = yield select(state => state.getIn(['tables', tableKey]));

    // Skip this process if the table hasn't been mounted yet, or trying to
    // reload the page before it's been configured
    if (!tableData || (type === 'RELOAD_PAGE' && !tableData.get('configured')))
      return;

    const response = yield call(calculateRows, tableData);

    const isPageEmpty = isClientSide(tableData, true)
      ? response?.data?.size <= tableData.get('pageOffset') &&
        tableData.get('pageOffset') > 0
      : response?.data?.size === 0 && tableData.get('pageTokens').size !== 0;

    if (isPageEmpty && !isClientSide(tableData, true)) {
      // If there is no data and a server side previous page exists, load the
      // previous page instead of setting the current response into state
      yield put({
        type: 'PREV_PAGE',
        payload: { tableKey },
      });
    } else {
      const { rows, data, nextPageToken, count, error, extraData } = response;
      const onFetch = tableData.get('onFetch');

      if (error) {
        yield put({
          type: 'SET_ROWS',
          payload: {
            tableKey,
            error,
            rows: List(),
            data: List(),
            nextPageToken: null,
            count: null,
            extraData,
          },
        });
      } else {
        yield put({
          type: 'SET_ROWS',
          payload: { tableKey, rows, data, nextPageToken, count, extraData },
        });
        if (isPageEmpty && isClientSide(tableData, true)) {
          // If there is no data and a client side previous page exists, load
          // the previous page after we set the updated data
          yield put({
            type: 'PREV_PAGE',
            payload: { tableKey },
          });
        }
      }

      if (typeof onFetch === 'function') {
        yield call(onFetch, { tableKey, rows, count, error, extraData });
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function* configureTableTask({ payload }) {
  const { filterForm, tableKey, initialFilterValues } = payload;
  if (!filterForm) {
    yield put(action('APPLY_FILTERS', { tableKey }));
  } else {
    yield put(
      action('APPLY_FILTER_FORM', {
        tableKey,
        appliedFilters: Map(initialFilterValues),
      }),
    );
  }
}

const pollerTableKeys = {};
function* pollingTask(tableKey) {
  while (true) {
    yield delay(10000);
    refetchTable(tableKey);
  }
}
function* startPollingTask({ payload }) {
  const { refreshInterval, tableKey } = payload;
  if (refreshInterval && !pollerTableKeys[tableKey]) {
    pollerTableKeys[tableKey] = yield fork(
      pollingTask,
      tableKey,
      refreshInterval,
    );
  }
}
function* stopPollingTask({ payload }) {
  const tableKey = payload.tableKey;
  if (pollerTableKeys[tableKey]) {
    yield cancel(pollerTableKeys[tableKey]);
    pollerTableKeys[tableKey] = null;
  }
}

regSaga('CONFIGURE_TABLE', function* () {
  yield takeEvery('CONFIGURE_TABLE', configureTableTask);
  yield takeEvery('CONFIGURE_TABLE', startPollingTask);
});
//
regSaga(takeEvery('UNMOUNT_TABLE', stopPollingTask));
regSaga(takeEvery('NEXT_PAGE', calculateRowsTask));
regSaga(takeEvery('PREV_PAGE', calculateRowsTask));
regSaga(takeEvery('GOTO_PAGE', calculateRowsTask));
regSaga(takeEvery('RELOAD_PAGE', calculateRowsTask));
regSaga(takeEvery('APPLY_FILTERS', calculateRowsTask));
regSaga(takeEvery('APPLY_FILTER_FORM', calculateRowsTask));
regSaga(takeEvery('REFETCH_TABLE_DATA', calculateRowsTask));

regSaga(
  takeEvery('SORT_COLUMN', function* (action) {
    const tableKey = action.payload.tableKey;
    // Call calculate rows task
    yield call(calculateRowsTask, action);

    // Get sort data and call callback
    const [onColumnSort, sortColumn, sortDirection] = yield select(state => [
      state.getIn(['tables', tableKey, 'onColumnSort']),
      state.getIn(['tables', tableKey, 'sortColumn', 'value']),
      state.getIn(['tables', tableKey, 'sortDirection']),
    ]);

    if (typeof onColumnSort === 'function') {
      yield call(onColumnSort, { tableKey, sortColumn, sortDirection });
    }
  }),
);

regSaga(
  takeEvery('TOGGLE_COLUMN', function* ({ payload: { tableKey } }) {
    const [onColumnToggle, columnSet] = yield select(state => [
      state.getIn(['tables', tableKey, 'onColumnToggle']),
      state.getIn(['tables', tableKey, 'columnSet']),
    ]);

    if (typeof onColumnToggle === 'function') {
      yield call(onColumnToggle, { tableKey, columnSet });
    }
  }),
);

regSaga(
  takeEvery('TOGGLE_FILTER', function* ({ payload: { tableKey, filters } }) {
    const [filterSet, appliedFilters] = yield select(state => [
      state.getIn(['tables', tableKey, 'filterSet']),
      state.getIn(['tables', tableKey, 'appliedFilters']),
    ]);

    // Determine filters that were removed
    const filtersToRemove = filters.filter(
      filter => !filterSet.includes(filter),
    );
    // If any filters were removed from the set
    if (filtersToRemove.length > 0) {
      for (const filter of filtersToRemove) {
        // Clear the filter field value in the form
        yield call(setValue, filterFormKey(tableKey), filter, null);
      }

      // If any of the removed filters were applied
      if (filtersToRemove.some(filter => appliedFilters.get(filter))) {
        // Submit the filter form to redo the query
        yield call(submitForm, filterFormKey(tableKey), {
          fieldSet: filterSet,
        });
      }
    }
  }),
);

export const operations = Map({
  includes: (cv, v) => cv.toLocaleLowerCase().includes(v.toLocaleLowerCase()),
  startsWith: (cv, v) =>
    cv.toLocaleLowerCase().startsWith(v.toLocaleLowerCase()),
  equals: (cv, v) =>
    typeof cv === 'string'
      ? cv.toLocaleLowerCase() === v.toLocaleLowerCase()
      : cv === v,
  lt: (cv, v) => cv < v,
  lteq: (cv, v) => cv <= v,
  gt: (cv, v) => cv > v,
  gteq: (cv, v) => cv >= v,
  between: (cv, v) => cv >= v.get(0) && cv < v.get(1),
  in: (cv, v) =>
    v.reduce(
      (found, value) =>
        found ||
        (typeof value === 'string'
          ? cv.toLocaleLowerCase() === value.toLocaleLowerCase()
          : cv === value),
      false,
    ),
});

export const isValueEmpty = value => {
  if (value === null) {
    return true;
  } else if (typeof value === 'undefined') {
    return true;
  } else if (List.isList(value)) {
    if (value.isEmpty()) {
      return true;
    } else {
      return value.reduce((empty, v) => empty && (!v || v === ''), true);
    }
  } else if (value === '') {
    return true;
  }
  return !value;
};

export const clientSideRowFilter = (row, filters) => {
  const usableFilters = filters
    .filter(filter => filter.get('value') !== '')
    .map((filter, column) => filter.set('currentValue', row.get(column)));

  return usableFilters.size === 0
    ? true
    : usableFilters.reduce((has, filter) => {
        const currentValue = filter.get('currentValue');
        const op = operations.get(
          filter.getIn(['column', 'filter']),
          () => true,
        );
        const value = filter.get('value');

        return isValueEmpty(value) ? has : op(currentValue, value);
      }, true);
};

const applyClientSideFilters = (tableData, data) => {
  const pageOffset = tableData.get('pageOffset');
  const pageSize = tableData.get('pageSize');
  const sortColumn = tableData.get('sortColumn');
  const sortDirection = tableData.get('sortDirection');
  const filters = tableData.get('appliedFilters');
  const startIndex = pageOffset;
  const endIndex = Math.min(pageOffset + pageSize, data.size);
  const dataSource = getDataSource(tableData);

  // This is because the `defineFilter` function wants a native object
  // and not an Immutable map. This changes when we stop using the
  // `clientSideRowFilter` legacy function or when we support immutable
  // objects in the `defineFilter`.
  const clientSideFilters = filters.toJS();
  const rowFilter =
    !dataSource || dataSource.clientSideSearch === true
      ? row => clientSideRowFilter(row, filters)
      : typeof dataSource.clientSide === 'function'
        ? row => dataSource.clientSide(row.toJS(), clientSideFilters)
        : () => true;

  return List(data)
    .map(d => Map(d))
    .update(d => d.filter(rowFilter))
    .update(d =>
      sortColumn ? d.sortBy(r => r.get(sortColumn.get('value'))) : d,
    )
    .update(d => (sortDirection === 'asc' ? d : d.reverse()))
    .update(d => d.slice(startIndex, endIndex));
};

const transformData = (data, tableData) =>
  data.map(row => {
    const columns = tableData.get('columns');
    return columns.reduce((rowData, column) => {
      const valueTransform = column.get('valueTransform', v => v);

      return rowData.update(column.get('value'), value =>
        valueTransform(value, row),
      );
    }, row);
  });

const calculateRows = tableData => {
  const dataSource = getDataSource(tableData);
  if (isClientSide(tableData)) {
    const data = tableData.get('data');
    const transformedRows = transformData(data, tableData);
    const rows = applyClientSideFilters(tableData, transformedRows);

    return Promise.resolve({
      rows,
      data,
      count: data.size,
    });
  } else if (dataSource) {
    const paramData = {
      pageSize: tableData.get('pageSize'),
      filters: tableData.get('appliedFilters'),
      sortColumn: tableData.getIn(['sortColumn', 'value']),
      sortDirection: tableData.get('sortDirection'),
      nextPageToken: tableData.get('nextPageToken'),
      pageTokens: tableData.get('pageTokens'),
    };
    const transform = dataSource.transform || (result => result);
    const params = dataSource.params(paramData);

    return dataSource.fn(...params).then(response => {
      if (response.error) return response;

      const {
        nextPageToken,
        data: responseData,
        count,
        extraData,
      } = transform(response, paramData);
      const data = fromJS(responseData);
      const transformedRows = transformData(data, tableData);
      const rows =
        dataSource.clientSideSearch || dataSource.clientSide
          ? applyClientSideFilters(tableData, transformedRows)
          : transformedRows;

      return {
        nextPageToken,
        count:
          dataSource.clientSideSearch || dataSource.clientSide
            ? data.size
            : count,
        data,
        rows,
        extraData,
      };
    });
  } else {
    throw new Error(
      'Unable to calculate rows: Missing both data and a dataSource!',
    );
  }
};

export const filterFormKey = tableKey => `${tableKey}.filter-form`;

export const mountTable = tableKey => {
  dispatch('MOUNT_TABLE', { tableKey });
  mountForm(filterFormKey(tableKey));
};
export const unmountTable = tableKey => {
  dispatch('UNMOUNT_TABLE', { tableKey });
  unmountForm(filterFormKey(tableKey));
};
export const configureTable = payload => {
  dispatch('CONFIGURE_TABLE', payload);
};
export const refetchTable = tableKey =>
  dispatch('REFETCH_TABLE_DATA', { tableKey });
export const reloadTablePage = tableKey =>
  dispatch('RELOAD_PAGE', { tableKey });
export const clearFilters = tableKey =>
  dispatch('CLEAR_TABLE_FILTERS', { tableKey });
export const resetFilterForm = tableKey =>
  dispatch('APPLY_FILTER_FORM', { tableKey, appliedFilters: Map() });
export const toggleFilterField = (tableKey, ...filters) =>
  dispatch('TOGGLE_FILTER', { tableKey, filters });

export const hasTableFiltersApplied = tableKey =>
  store
    .getState()
    .getIn(['tables', tableKey, 'appliedFilters'])
    ?.filter(Boolean)?.size > 0;

export const findTableKey = fn =>
  store
    .getState()
    .get('tables')
    .keySeq()
    .toList()
    .find(fn || (() => true));
